import { useState, useEffect, FormEvent } from 'react'
import { useParams, Link } from 'react-router-dom'
import { apiFetch } from '@/lib/apiFetch'
import SpellingListEditor from '@/components/SpellingListEditor'

interface SpellingWord {
  id: number
  text: string
  hint: string | null
}

interface SpellingMastery {
  dailyCorrect: number
  dailyFailed: boolean
  dailyClearedAt: number | null
  masteredAt: number | null
  totalAttempts: number
  totalCorrect: number
}

interface SpellingListDetail {
  list: { id: number; childId: number; name: string; isActive: boolean; createdAt: number; archivedAt: number | null }
  words: Array<{ word: SpellingWord; mastery: SpellingMastery | null }>
  masteredCount: number
}

interface SpellingListSummary {
  list: { id: number; name: string; isActive: boolean; createdAt: number; archivedAt: number | null }
  wordCount: number
  masteredCount: number
}

interface GameDifficultyEntry {
  exerciseType: string
  difficultyLevel: number
}

const GAME_INFO: Record<string, { name: string; category: 'Reading' | 'Music' }> = {
  WORD_DECODE: { name: 'Word Decode', category: 'Reading' },
  SYNONYM_MATCH: { name: 'Synonym Match', category: 'Reading' },
  SYNONYM_PAIRS: { name: 'Synonym Pairs', category: 'Reading' },
  SPELLING: { name: 'Spelling', category: 'Reading' },
  NOTE_NAMING: { name: 'Note Naming', category: 'Music' },
  INTERVAL_RECOGNITION: { name: 'Interval Recognition', category: 'Music' },
  EAR_TRAINING: { name: 'Ear Training', category: 'Music' },
  MELODIC_DICTATION: { name: 'Melodic Dictation', category: 'Music' },
  HARMONIC_DICTATION: { name: 'Harmonic Dictation', category: 'Music' },
}

const LEVEL_LABELS = ['', 'Easiest', 'Easy', 'Medium', 'Hard', 'Hardest']

export default function GameSettingsPage() {
  const { childId } = useParams<{ childId: string }>()
  const numChildId = Number(childId)

  const [lists, setLists] = useState<SpellingListSummary[]>([])
  const [activeDetail, setActiveDetail] = useState<SpellingListDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [showEditor, setShowEditor] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const [clefPreference, setClefPreference] = useState<string>('TREBLE')
  const [musicLoading, setMusicLoading] = useState(true)
  const [difficulties, setDifficulties] = useState<GameDifficultyEntry[]>([])
  const [difficultyLoading, setDifficultyLoading] = useState(true)

  const loadLists = async () => {
    try {
      const data = await apiFetch(`/spelling/lists?childId=${numChildId}`)
      setLists(data)

      const active = data.find((d: SpellingListSummary) => d.list.isActive)
      if (active) {
        const detail = await apiFetch(`/spelling/lists/${active.list.id}`)
        setActiveDetail(detail)
      } else {
        setActiveDetail(null)
      }
    } catch {
      setLists([])
      setActiveDetail(null)
    } finally {
      setLoading(false)
    }
  }

  const loadMusicSettings = async () => {
    try {
      const data = await apiFetch(`/music/settings?childId=${numChildId}`)
      setClefPreference(data.clefPreference)
    } catch {}
    setMusicLoading(false)
  }

  const loadDifficulties = async () => {
    try {
      const data: GameDifficultyEntry[] = await apiFetch(`/game-difficulty?childId=${numChildId}`)
      setDifficulties(data)
    } catch {}
    setDifficultyLoading(false)
  }

  useEffect(() => {
    loadLists()
    loadMusicSettings()
    loadDifficulties()
  }, [numChildId])

  const handleClefChange = async (value: string) => {
    setClefPreference(value)
    try {
      await apiFetch(`/music/settings?childId=${numChildId}`, {
        method: 'PUT',
        body: JSON.stringify({ clefPreference: value }),
      })
    } catch {
      loadMusicSettings()
    }
  }

  const getDifficultyLevel = (exerciseType: string): number => {
    const entry = difficulties.find(d => d.exerciseType === exerciseType)
    return entry ? entry.difficultyLevel : 1.0
  }

  const handleDifficultyChange = async (exerciseType: string, level: number) => {
    setDifficulties(prev => {
      const existing = prev.find(d => d.exerciseType === exerciseType)
      if (existing) {
        return prev.map(d => d.exerciseType === exerciseType ? { ...d, difficultyLevel: level } : d)
      }
      return [...prev, { exerciseType, difficultyLevel: level }]
    })
    try {
      await apiFetch(`/game-difficulty?childId=${numChildId}`, {
        method: 'PUT',
        body: JSON.stringify({ exerciseType, difficultyLevel: level }),
      })
    } catch {
      loadDifficulties()
    }
  }

  const handleSaved = () => {
    setShowEditor(false)
    setLoading(true)
    loadLists()
  }

  const handleDelete = async (listId: number) => {
    try {
      await apiFetch(`/spelling/lists/${listId}`, { method: 'DELETE' })
      setLoading(true)
      loadLists()
    } catch {}
  }

  const [newWord, setNewWord] = useState('')
  const [addingWord, setAddingWord] = useState(false)

  const handleAddWord = async (e: FormEvent) => {
    e.preventDefault()
    if (!activeDetail || !newWord.trim()) return
    setAddingWord(true)
    try {
      await apiFetch(`/spelling/lists/${activeDetail.list.id}/words`, {
        method: 'POST',
        body: JSON.stringify({ text: newWord.trim() }),
      })
      setNewWord('')
      loadLists()
    } catch {}
    setAddingWord(false)
  }

  const handleRemoveWord = async (wordId: number) => {
    try {
      await apiFetch(`/spelling/words/${wordId}`, { method: 'DELETE' })
      loadLists()
    } catch {}
  }

  const activeList = lists.find(l => l.list.isActive)
  const archivedLists = lists.filter(l => !l.list.isActive)

  const renderDifficultyGroup = (category: 'Reading' | 'Music') => {
    const games = Object.entries(GAME_INFO).filter(([, info]) => info.category === category)
    return (
      <div className="space-y-3">
        {games.map(([type, info]) => {
          const level = getDifficultyLevel(type)
          const rounded = Math.round(level)
          return (
            <div key={type} className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium">{info.name}</p>
                <p className="text-xs text-muted-foreground">
                  Level {level.toFixed(1)} — {LEVEL_LABELS[rounded] || 'Easiest'}
                </p>
              </div>
              <div className="flex gap-1 shrink-0">
                {[1, 2, 3, 4, 5].map(n => (
                  <button
                    key={n}
                    onClick={() => handleDifficultyChange(type, n)}
                    className={`w-9 h-9 rounded-lg text-sm font-bold transition min-h-[44px] min-w-[44px] ${
                      rounded === n
                        ? 'bg-primary text-primary-foreground'
                        : 'border border-border hover:bg-accent'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
      <div className="flex items-center gap-3">
        <Link
          to="/settings"
          className="text-sm text-muted-foreground hover:text-foreground transition"
        >
          &larr; Settings
        </Link>
        <h1 className="text-2xl font-bold">Game Settings</h1>
      </div>

      {/* Game Difficulty Section */}
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Game Difficulty</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Difficulty adjusts automatically as your child plays. You can also set it manually here.
          </p>
        </div>
        {difficultyLoading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : (
          <div className="border border-border rounded-lg p-4 space-y-5">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-3">Reading</p>
              {renderDifficultyGroup('Reading')}
            </div>
            <div className="border-t border-border pt-4">
              <p className="text-sm font-medium text-muted-foreground mb-3">Music</p>
              {renderDifficultyGroup('Music')}
            </div>
          </div>
        )}
      </div>

      {/* Spelling Words Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Spelling Words</h2>
          <button
            onClick={() => setShowEditor(!showEditor)}
            className="text-sm px-3 py-1.5 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition min-h-[44px]"
          >
            {showEditor ? 'Cancel' : 'New List'}
          </button>
        </div>

        {showEditor && (
          <SpellingListEditor
            childId={numChildId}
            hasActiveList={!!activeList}
            onSaved={handleSaved}
            onCancel={() => setShowEditor(false)}
          />
        )}

        {/* Active list */}
        {activeDetail ? (
          <div className="border border-border rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{activeDetail.list.name}</p>
                <p className="text-sm text-muted-foreground">
                  {activeDetail.masteredCount} of {activeDetail.words.length} mastered
                </p>
              </div>
              <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full font-medium">
                Active
              </span>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-accent rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${(activeDetail.masteredCount / Math.max(activeDetail.words.length, 1)) * 100}%` }}
              />
            </div>

            {/* Word grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {activeDetail.words.map(({ word, mastery }) => (
                <div
                  key={word.id}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${
                    mastery?.masteredAt
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-accent/50 border border-border'
                  }`}
                >
                  <span className="font-medium">{word.text}</span>
                  <div className="flex items-center gap-1.5">
                    {mastery?.masteredAt ? (
                      <span className="text-green-600">✅</span>
                    ) : mastery?.dailyClearedAt ? (
                      <span className="text-primary text-xs">🎉</span>
                    ) : mastery?.dailyFailed ? (
                      <span className="text-amber-500 text-xs">❌ {mastery.dailyCorrect}/3</span>
                    ) : mastery ? (
                      <span className="text-muted-foreground text-xs">{mastery.dailyCorrect}/3</span>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                    <button
                      onClick={() => handleRemoveWord(word.id)}
                      className="text-muted-foreground hover:text-red-500 transition ml-1 text-xs"
                      title="Remove word"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Add word inline */}
            <form onSubmit={handleAddWord} className="flex gap-2">
              <input
                type="text"
                value={newWord}
                onChange={e => setNewWord(e.target.value)}
                placeholder="Add a word..."
                className="flex-1 px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />
              <button
                type="submit"
                disabled={addingWord || !newWord.trim()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition disabled:opacity-50 min-h-[44px]"
              >
                Add
              </button>
            </form>
          </div>
        ) : !showEditor ? (
          <div className="border border-border rounded-lg p-6 text-center">
            <p className="text-muted-foreground text-sm">
              No spelling list yet. Click "New List" to add your child's spelling words.
            </p>
            <p className="text-muted-foreground text-xs mt-2">
              Without a custom list, the game uses words from their reading level.
            </p>
          </div>
        ) : null}

        {/* Archived lists */}
        {archivedLists.length > 0 && (
          <div>
            <button
              onClick={() => setShowArchived(!showArchived)}
              className="text-sm text-muted-foreground hover:text-foreground transition"
            >
              {showArchived ? 'Hide' : 'Show'} archived lists ({archivedLists.length})
            </button>

            {showArchived && (
              <div className="mt-3 space-y-2">
                {archivedLists.map(({ list, wordCount, masteredCount }) => (
                  <div
                    key={list.id}
                    className="flex items-center justify-between px-4 py-3 border border-border rounded-lg text-sm"
                  >
                    <div>
                      <p className="font-medium text-muted-foreground">{list.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {masteredCount}/{wordCount} mastered
                        {list.archivedAt && (
                          <> &middot; archived {new Date(list.archivedAt).toLocaleDateString()}</>
                        )}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDelete(list.id)}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Music Settings Section */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Music Settings</h2>
        {musicLoading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : (
          <div className="border border-border rounded-lg p-4 space-y-3">
            <div>
              <p className="text-sm font-medium mb-2">Clef</p>
              <div className="flex gap-2">
                {[
                  { value: 'TREBLE', label: 'Treble Only' },
                  { value: 'BASS', label: 'Bass Only' },
                  { value: 'BOTH', label: 'Both' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleClefChange(opt.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition min-h-[44px] ${
                      clefPreference === opt.value
                        ? 'bg-primary text-primary-foreground'
                        : 'border border-border hover:bg-accent'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Controls which clef(s) appear in music exercises.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
